import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { RequestService } from 'src/app/services/request.service';
import { SidebarService } from 'src/app/services/sidebar.service';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { Observable, tap, of, take, filter } from 'rxjs';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { ShareDataService } from 'src/app/services/share-data.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: false
})
export class SidebarComponent implements OnInit, AfterViewInit {
  screenWidth: number;
  admin: boolean = false;
  subadmin: boolean = false;
  superuser: boolean = false;
  farmers: any[] = [];
  kydNames: string[] = [];
  farmersByKyd: { [key: string]: any[] } = {};
  isRecommendOpen: boolean = false;
  selectedFarmer: string = '';
  selectedKyd: string = '';
  farmerSearchControls: { [key: string]: FormControl } = {};
  filteredFarmers: { [key: string]: Observable<any[]> } = {};
  currentFarmerSearchControl: FormControl = new FormControl('');
  currentFilteredFarmers: Observable<any[]> = of([]);
  farmerData: any;
  osdeYears: any = [];
  selectedYear: string = '';
  isCropManagementOpen: boolean = false;
  isStorageManagementOpen: boolean = false;
  isCultivationSupportOpen: boolean = false;

  @ViewChild('sidenav') public sidebar!: MatSidenav;

  constructor(
    private router:Router,
    private sidebarService: SidebarService,
    private authorizationService: AuthorizationService,
    private req: RequestService<any>,
    public featureFlags: FeatureFlagsService,
    private shareDataService: ShareDataService
  ) {
    this.screenWidth = window.innerWidth;
    window.onresize = () => {
      this.screenWidth = window.innerWidth;
    };
  }

  toggleCropManagement() {
    this.isCropManagementOpen = !this.isCropManagementOpen;
  }

  toggleStorageManagement() {
    this.isStorageManagementOpen = !this.isStorageManagementOpen;
  }

  toggleCultivationSupport() {
    this.isCultivationSupportOpen = !this.isCultivationSupportOpen;
  }

  ngOnInit() {
    this.getFarmerData();
    
    // Load stored selections from sessionStorage (only if valid session)
    const storedKyd = sessionStorage.getItem('selectedKyd');
    if (storedKyd) {
      this.selectedKyd = storedKyd;
    }
    
    const storedFarmer =sessionStorage.getItem('selectedFarmer');
    if (storedFarmer) {
      this.selectedFarmer = storedFarmer;
    }

    // Roles should not depend on userRelationship loading
    // Subscribe to updates so late role resolution still updates the UI.
    this.authorizationService.superuser$.subscribe(superuser => this.superuser = superuser);
    this.authorizationService.admin$.subscribe(admin => this.admin = admin);
    this.authorizationService.subadmin$.subscribe(subadmin => this.subadmin = subadmin);

    this.shareDataService.getData<any>('userRelationship').pipe(
      filter(data => !!data),
      take(1),
      tap(res => {
        const relationships = Array.isArray(res) ? res : [res];
        const farmers = relationships?.[0]?.related_farmers ?? [];
        if (farmers.length) {
          this.processFarmers(farmers);
        }

        // Update the control after farmers are processed
        if (this.selectedKyd) {
          this.updateCurrentFarmersControl();
        }
      }),
    ).subscribe();
  }

  ngAfterViewInit() {
    this.sidebarService.setSidenav(this.sidebar);
  }


  getFarmerData() {
    this.shareDataService.getData<any>('farmer').subscribe((data) => {
      if (data) {
        this.farmerData = data;
        const years = this.farmerData.opekepe_data.map((e: any) => e.year_osde);
        this.osdeYears = [...new Set(years)];

        const selectedYear = this.farmerData.selected_year_data;
        if (selectedYear) {
          this.selectedYear = selectedYear;
        }
      }
    });
  }

  processFarmers(farmers: any[]) {
    this.farmers = farmers;
    this.kydNames = [...new Set(farmers.map(f => f.kyd_name))];

    this.kydNames.forEach(kyd => {
      this.farmersByKyd[kyd] = this.farmers.filter(farmer => farmer.kyd_name === kyd);

      this.farmerSearchControls[kyd] = new FormControl('');

      this.filteredFarmers[kyd] = this.farmerSearchControls[kyd].valueChanges.pipe(
        startWith(''),
        map(value => this.filterFarmers(value, kyd))
      );
    });
  }

  filterFarmers(value: string, kyd: string): any[] {
    const filterValue = value.trim().toLowerCase();
    if (!filterValue) {
      return this.farmersByKyd[kyd];
    }
    return this.farmersByKyd[kyd].filter(farmer => {
      const first = (farmer.first_name || '').toLowerCase();
      const last = (farmer.last_name || '').toLowerCase();
      const fullFirstLast = `${first} ${last}`;
      const fullLastFirst = `${last} ${first}`;
      return (
        first.startsWith(filterValue) ||
        last.startsWith(filterValue) ||
        fullFirstLast.includes(filterValue) ||
        fullLastFirst.includes(filterValue)
      );
    });
  }

  getFarmers(kyd: string) {
    return this.farmersByKyd[kyd] || [];
  }

  toggleRecommendations() {
    this.isRecommendOpen = !this.isRecommendOpen;
  }

  selectKyd(kyd: string) {
    this.selectedKyd = kyd;
    this.selectedFarmer = '';
   sessionStorage.setItem('selectedKyd', this.selectedKyd);
    sessionStorage.removeItem('selectedFarmer');
    this.updateCurrentFarmersControl();
  }

  updateCurrentFarmersControl() {
    if (this.selectedKyd) {
      // Get or create the form control for this KYD
      if (!this.farmerSearchControls[this.selectedKyd]) {
        this.farmerSearchControls[this.selectedKyd] = new FormControl('');
      }
      this.currentFarmerSearchControl = this.farmerSearchControls[this.selectedKyd];
      
      // Set up the filtered farmers observable with initial empty value to show all farmers
      this.currentFilteredFarmers = this.currentFarmerSearchControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterFarmers(value || '', this.selectedKyd))
      );
      
      // Trigger initial emission to show all farmers
      this.currentFarmerSearchControl.setValue('', { emitEvent: true });
    }
  }

  selectFarmer(farmer: any) {
    this.selectedFarmer = `${farmer.first_name} ${farmer.last_name}`;
    sessionStorage.setItem('selectedFarmer', this.selectedFarmer);

    let updatedData = {
      selected_keycloak_id: farmer.keycloak_id,
    };

    this.req.updateSelectedFarmer(updatedData).subscribe({
      next: () => {
        location.reload();
      }
    });
  }

  selectYear(year: any) {
    this.selectedYear = year;

    let updatedData = {
      selected_year_data: year
    }

    this.req.updateOsdeYear(updatedData).subscribe({
      next: () => {
        location.reload();
      }
    });
  }

navigateToPests(tabType: 'fertilizers' | 'diseases'): void {
  const currentUrl = this.router.url;
  const targetUrl = '/pests';
  
  // Save tab type to session storage
  sessionStorage.setItem('pestsTabType', tabType);
  
  // If we're already on the pests page, force reload
  if (currentUrl.startsWith('/pests')) {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([targetUrl], {
        queryParams: { tab: tabType }
      });
    });
  } else {
    // Normal navigation
    this.router.navigate([targetUrl], {
      queryParams: { tab: tabType }
    });
  }
}
}
